import {
  actionDateInputValue,
  actionTodayDateKey,
} from '@brightnav/care-plan';
import { DueDateFilter } from './http/action-api.model';

/**
 * Exclusive chip/list due-date buckets. Timeline drills (`day`, `beyond`)
 * are not chips; they round-trip through the List due-date dropdown.
 */
export type DueDateBucket = Exclude<DueDateFilter, 'all' | 'day' | 'beyond'>;

/**
 * Product calendar key (`YYYY-MM-DD` America/New_York), matching Care Plan
 * {@link actionDateInputValue} / {@link actionTodayDateKey}. Date-only AIM
 * strings stay as stored; instants use the Eastern civil day.
 */
export function dueDateKey(
  value: string | Date | null | undefined,
): string {
  if (value == null || value === '') {
    return '';
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : actionTodayDateKey(value);
  }
  return actionDateInputValue(value);
}

/**
 * Adds calendar days to a `YYYY-MM-DD` key without using the browser TZ.
 *
 * @param dayKey - Product date key.
 * @param days - Days to shift (may be negative).
 * @returns Shifted `YYYY-MM-DD`, or `''` when `dayKey` is invalid.
 */
export function addCalendarDayKey(dayKey: string, days: number): string {
  const [year, month, day] = dayKey.split('-').map(Number);
  if (!year || !month || !day) {
    return '';
  }
  return new Date(Date.UTC(year, month - 1, day + days))
    .toISOString()
    .slice(0, 10);
}

/** Monday of the week that contains `dayKey` (Monday-start product week). */
export function startOfMondayWeekKey(dayKey: string): string {
  const [year, month, day] = dayKey.split('-').map(Number);
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(utc.getTime())) {
    return '';
  }
  const weekday = utc.getUTCDay();
  const daysFromMonday = weekday === 0 ? 6 : weekday - 1;
  return addCalendarDayKey(dayKey, -daysFromMonday);
}

/**
 * Maps a due date onto exclusive Home buckets: overdue, today, remainder of
 * this Monday–Sunday week, next Monday–Sunday week, then later. Comparisons
 * use the Eastern product day so Home matches Care Plan overdue.
 */
export function dueBucket(
  value: string | Date | null | undefined,
  now = new Date(),
): DueDateBucket | null {
  const due = dueDateKey(value);
  if (!due) {
    return null;
  }
  const today = actionTodayDateKey(now);
  if (due < today) {
    return 'overdue';
  }
  if (due === today) {
    return 'today';
  }
  const nextMonday = addCalendarDayKey(startOfMondayWeekKey(today), 7);
  if (due < nextMonday) {
    return 'week';
  }
  const weekAfter = addCalendarDayKey(nextMonday, 7);
  if (due < weekAfter) {
    return 'nextWeek';
  }
  return 'later';
}

/**
 * True when the due date is on or after today + `horizonDays` (Timeline 3+ wks).
 * That cutoff can differ from the Later chip when today is not Monday.
 */
export function isBeyondHorizon(
  value: string | Date | null | undefined,
  now = new Date(),
  horizonDays = 14,
): boolean {
  const due = dueDateKey(value);
  if (!due) {
    return false;
  }
  return due >= addCalendarDayKey(actionTodayDateKey(now), horizonDays);
}

/**
 * Client-side due-date match so Timeline chip counts can stay on the full
 * loaded set while List/chart views slice by bucket, day, or 14-day horizon.
 */
export function actionMatchesDueFilter(
  value: string | Date | null | undefined,
  filter: DueDateFilter,
  now = new Date(),
  dayKey?: string | null,
): boolean {
  if (filter === 'all') {
    return true;
  }
  if (filter === 'day') {
    const due = dueDateKey(value);
    return !!due && !!dayKey && due === dayKey;
  }
  if (filter === 'beyond') {
    return isBeyondHorizon(value, now);
  }
  return dueBucket(value, now) === filter;
}
